(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => [...context.querySelectorAll(selector)];
  const body = document.body;
  const overlay = qs('[data-mobile-overlay]');
  const toast = qs('[data-toast]');
  let toastTimer;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[character]));

  const formatMoney = (cents) => {
    const value = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: 'USD' }).format(value);
    } catch (error) {
      return `$${value.toFixed(2)}`;
    }
  };

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('is-visible');
    window.clearTimeout(toastTimer);
    toastTimer = window.setTimeout(() => toast.classList.remove('is-visible'), 2800);
  };

  const closeDrawers = () => {
    qs('[data-mobile-menu]')?.classList.remove('is-open');
    qs('[data-filter-drawer]')?.classList.remove('is-open');
    overlay?.classList.remove('is-visible');
    body.classList.remove('menu-open', 'filter-open');
    qs('[data-mobile-menu]')?.setAttribute('aria-hidden', 'true');
    qs('[data-mobile-menu]')?.setAttribute('inert', '');
    qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
  };

  const mobileMenu = qs('[data-mobile-menu]');
  qs('[data-menu-toggle]')?.addEventListener('click', () => {
    const opening = !mobileMenu?.classList.contains('is-open');
    closeDrawers();
    if (opening && mobileMenu) {
      mobileMenu.classList.add('is-open');
      mobileMenu.setAttribute('aria-hidden', 'false');
      mobileMenu.removeAttribute('inert');
      qs('[data-menu-close]')?.focus();
      overlay?.classList.add('is-visible');
      body.classList.add('menu-open');
      qs('[data-menu-toggle]')?.setAttribute('aria-expanded', 'true');
    }
  });
  qs('[data-menu-close]')?.addEventListener('click', closeDrawers);
  overlay?.addEventListener('click', closeDrawers);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape') closeDrawers(); });

  const headerSearch = qs('.header-search');
  qs('[data-search-toggle]')?.addEventListener('click', () => {
    headerSearch?.classList.toggle('is-open');
    if (headerSearch?.classList.contains('is-open')) qs('[data-predictive-input]', headerSearch)?.focus();
  });

  qsa('[data-predictive-search]').forEach((container) => {
    const input = qs('[data-predictive-input]', container);
    const results = qs('[data-predictive-results]', container);
    if (!input || !results) return;
    let timer;
    let controller;

    const clear = () => { results.hidden = true; results.innerHTML = ''; };
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { clear(); return; }
      timer = window.setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${window.routes.predictive_search_url}.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=last`;
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) throw new Error('Search request failed');
          const data = await response.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = '<div class="predictive-results__footer">No matching fragrances found</div>';
          } else {
            results.innerHTML = products.map((product) => `
              <a class="predictive-results__item" href="${escapeHtml(product.url)}">
                ${product.image ? `<img src="${escapeHtml(product.image)}" width="62" height="74" alt="">` : '<span></span>'}
                <span><small>${escapeHtml(product.vendor || '')}</small><strong>${escapeHtml(product.title)}</strong></span>
                <strong>${escapeHtml(formatMoney(Math.round(Number(product.price || 0) * 100)))}</strong>
              </a>`).join('') + `<a class="predictive-results__footer" href="/search?q=${encodeURIComponent(query)}&type=product">View all results →</a>`;
          }
          results.hidden = false;
        } catch (error) {
          if (error.name !== 'AbortError') clear();
        }
      }, 220);
    });
    input.addEventListener('keydown', (event) => { if (event.key === 'Escape') clear(); });
    document.addEventListener('click', (event) => { if (!container.contains(event.target)) clear(); });
  });

  const updateCartCount = async () => {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      qsa('.cart-count').forEach((count) => {
        count.textContent = cart.item_count;
        count.classList.toggle('is-empty', cart.item_count === 0);
        count.closest('a')?.setAttribute('aria-label', `Cart with ${cart.item_count} items`);
      });
    } catch (error) {  }
  };

  document.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!form.matches('.quick-add-form')) return;
      event.preventDefault();
      const button = qs('button[type="submit"]', form);
      const original = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      try {
        const response = await fetch(window.routes.cart_add_url + '.js', {
          method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form)
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || 'Unable to add product');
        await updateCartCount();
        showToast(`${data.product_title || 'Fragrance'} added to cart`);
      } catch (error) {
        showToast(error.message || 'Unable to add product');
      } finally {
        if (button) { button.disabled = false; button.textContent = original; }
      }
  });

  const wishlistKey = 'costco-fragrance-wishlist';
  let wishlist = [];
  try { wishlist = JSON.parse(localStorage.getItem(wishlistKey) || '[]'); } catch (error) { wishlist = []; }
  const syncWishlistButtons = () => {
    qsa('[data-product-card]').forEach((card) => {
      const button = qs('[data-wishlist-button]', card);
      if (!button) return;
      const saved = wishlist.includes(card.dataset.productHandle);
      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', String(saved));
    });
  };
  syncWishlistButtons();
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-button]');
    const handle = button?.closest('[data-product-card]')?.dataset.productHandle;
    if (!handle) return;
    wishlist = wishlist.includes(handle) ? wishlist.filter((item) => item !== handle) : [...wishlist, handle];
    try { localStorage.setItem(wishlistKey, JSON.stringify(wishlist)); } catch (error) { showToast('Unable to save on this device'); }
    syncWishlistButtons();
    showToast(wishlist.includes(handle) ? 'Saved to your wishlist' : 'Removed from your wishlist');
  });

  qsa('[data-carousel-section]').forEach((section) => {
    const track = qs('[data-carousel-track]', section);
    if (!track) return;
    const scroll = (direction) => track.scrollBy({ left: direction * track.clientWidth * 0.82, behavior: 'smooth' });
    qs('[data-carousel-prev]', section)?.addEventListener('click', () => scroll(-1));
    qs('[data-carousel-next]', section)?.addEventListener('click', () => scroll(1));
  });

  qsa('[data-countdown]').forEach((timer) => {
    const hours = Math.max(1, Number(timer.dataset.countdown || 24));
    const storageKey = `deal-countdown-${hours}`;
    let end = Number(sessionStorage.getItem(storageKey));
    if (!end || end < Date.now()) {
      end = Date.now() + hours * 60 * 60 * 1000;
      sessionStorage.setItem(storageKey, String(end));
    }
    const target = qs('[data-countdown-value]', timer);
    const update = () => {
      const remaining = Math.max(0, end - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const h = String(Math.floor(totalSeconds / 3600)).padStart(2, '0');
      const m = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, '0');
      const s = String(totalSeconds % 60).padStart(2, '0');
      if (target) target.textContent = `${h}:${m}:${s}`;
    };
    update(); window.setInterval(update, 1000);
  });

  const filterDrawer = qs('[data-filter-drawer]');
  qs('[data-filter-toggle]')?.addEventListener('click', () => {
    filterDrawer?.classList.add('is-open'); overlay?.classList.add('is-visible'); body.classList.add('filter-open');
  });
  qs('[data-filter-close]')?.addEventListener('click', closeDrawers);
  qs('[data-sort-select]')?.addEventListener('change', (event) => { const url = new URL(window.location.href); url.searchParams.set('sort_by',event.target.value); url.searchParams.delete('page'); window.location.assign(url); });

  const productRoot = qs('[data-product-root]');
  if (productRoot) {
    qsa('[data-gallery-thumb]', productRoot).forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const index = thumb.dataset.galleryThumb;
        qsa('[data-gallery-thumb]', productRoot).forEach((item) => item.classList.toggle('is-active', item === thumb));
        qsa('[data-gallery-media]', productRoot).forEach((media) => media.classList.toggle('is-active', media.dataset.galleryMedia === index));
      });
    });

    const quantityInput = qs('[data-qty-input]', productRoot);
    qs('[data-qty-minus]', productRoot)?.addEventListener('click', () => { if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) - 1); });
    qs('[data-qty-plus]', productRoot)?.addEventListener('click', () => { if (quantityInput) quantityInput.value = Math.max(1, Number(quantityInput.value || 1) + 1); });

    const variantsScript = qs('[data-product-variants]', productRoot);
    const select = qs('[data-variant-select]', productRoot);
    let variants = [];
    try { variants = JSON.parse(variantsScript?.textContent || '[]'); } catch (error) { variants = []; }
    select?.addEventListener('change', () => {
      const variant = variants.find((item) => String(item.id) === String(select.value));
      if (!variant) return;
      const priceWrapper = qs('[data-price-wrapper]', productRoot);
      const currentPrice = qs('[data-product-price]', priceWrapper);
      const comparePrice = qs('[data-compare-price]', priceWrapper);
      const availability = qs('[data-product-availability]', productRoot);
      const addButton = qs('[data-add-to-cart]', productRoot);
      const sku = qs('[data-product-sku]', productRoot);
      const percent = variant.compare_at_price > variant.price ? Math.floor((variant.compare_at_price-variant.price)*100/variant.compare_at_price) : 0;
      const savings = qs('[data-product-savings]', productRoot);
      if(savings) { savings.hidden = !percent; savings.textContent = `You save ${formatMoney(variant.compare_at_price-variant.price)}`; }
      for(const badge of qsa('[data-product-discount], [data-price-save]', productRoot)) { badge.hidden = !percent; badge.textContent = badge.matches('[data-price-save]') ? `Save ${percent}%` : `-${percent}%`; }
      priceWrapper?.classList.toggle('price--sale', percent > 0);
      if (currentPrice) currentPrice.textContent = formatMoney(variant.price);
      if (comparePrice) {
        comparePrice.textContent = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : '';
        comparePrice.hidden = !(variant.compare_at_price > variant.price);
      }
      if (availability) {
        availability.classList.toggle('is-unavailable', !variant.available);
        availability.lastChild.textContent = variant.available ? 'In stock and ready to ship' : 'Currently unavailable';
      }
      if (addButton) { addButton.disabled = !variant.available; addButton.textContent = variant.available ? 'Add to cart' : 'Sold out'; }
      if (sku) sku.textContent = variant.sku || '—';
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); window.history.replaceState({}, '', url);
    });
  }

  qsa('[data-recommendations-url]').forEach(async (section) => {
    if (section.innerHTML.trim()) return;
    try {
      const response = await fetch(section.dataset.recommendationsUrl);
      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const replacement = qs('[data-recommendations-url]', documentFragment);
      if (replacement?.innerHTML.trim()) {
        section.innerHTML = replacement.innerHTML;
        syncWishlistButtons();
      }
    } catch (error) {  }
  });
  const savedGrid = qs('[data-wishlist-grid]');
  if (savedGrid) {
    const validHandles = wishlist.filter(handle => typeof handle === 'string' && /^[a-z0-9-]+$/.test(handle)).slice(0,100);
    Promise.allSettled(validHandles.map(async handle => {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return null;
      return response.json();
    })).then(results => {
      const products = results.filter(result=>result.status==='fulfilled' && result.value).map(result=>result.value);
      qs('[data-wishlist-empty]').hidden = products.length > 0;
      savedGrid.innerHTML = products.map(product => `<article class="product-card"><div class="product-card__media"><a href="/products/${escapeHtml(product.handle)}"><img class="product-card__image" src="${escapeHtml(product.featured_image || '')}" alt="${escapeHtml(product.title)}" loading="lazy"></a></div><div class="product-card__content"><span class="product-card__vendor">${escapeHtml(product.vendor)}</span><h2><a href="/products/${escapeHtml(product.handle)}">${escapeHtml(product.title)}</a></h2><p>${escapeHtml(formatMoney(product.price))}</p><a class="button button--quick" href="/products/${escapeHtml(product.handle)}">View fragrance</a><button class="button button--outline" data-remove-saved="${escapeHtml(product.handle)}">Remove</button></div></article>`).join('');
      savedGrid.addEventListener('click', event => {
        const button = event.target.closest('[data-remove-saved]');
        if (!button) return;
        wishlist = wishlist.filter(handle => handle !== button.dataset.removeSaved);
        try { localStorage.setItem(wishlistKey,JSON.stringify(wishlist)); } catch(error) {}
        button.closest('article').remove();
        qs('[data-wishlist-empty]').hidden = savedGrid.children.length > 0;
      });
    });
  }
})();
